import {
  IMPORT_ENTITY_LABELS,
  type ImportEntity,
  ImportEntity as ImportEntitySchema,
  type ImportIssue,
  ImportPreview,
  ImportRecord,
  ImportSpec,
  formatDate,
  formatTime,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { z } from 'zod';
import { Icon } from '../../../components/Icon';
import { Modal } from '../../../components/Overlays';
import { Alert } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api, newIdempotencyKey } from '../../../lib/api';
import { errorMessage } from '../../../lib/messages';
import { useIsAdmin } from '../../../lib/session';

const STEPS = ['Arquivo', 'Colunas', 'Simulação', 'Importar'] as const;
const LINKS: Record<ImportEntity, string> = {
  filamentos: '/filamentos',
  carreteis: '/filamentos/carreteis',
  insumos: '/insumos',
  produtos: '/produtos',
  despesas: '/despesas',
};

interface Loaded {
  name: string;
  text: string;
  rows: number;
  columns: number;
  separator: string;
}

/** Rough count for the file card; the API does the real parsing. */
function describeFile(name: string, text: string): Loaded {
  const lines = text
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim());
  const first = lines[0] ?? '';
  const sep = (first.match(/;/g)?.length ?? 0) >= (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  return {
    name,
    text,
    rows: Math.max(0, lines.length - 1),
    columns: first ? first.split(sep).length : 0,
    separator: sep === ';' ? 'ponto e vírgula' : 'vírgula',
  };
}

const csvCell = (v: string) => (/[;"\n]/.test(v) ? `"${v.replaceAll('"', '""')}"` : v);

function errorsCsv(issues: ImportIssue[]): string {
  const rows = [
    ['Linha', 'Coluna', 'Valor', 'Problema'],
    ...issues.map((i) => [String(i.line), i.column, i.value, i.problem]),
  ];
  return `\uFEFF${rows.map((r) => r.map(csvCell).join(';')).join('\r\n')}\r\n`;
}

/** "Importar" (board Importar-CSV). */
export function ImportTab() {
  const isAdmin = useIsAdmin();
  if (!isAdmin)
    return (
      <section className="card col" style={{ gap: 10, alignItems: 'flex-start' }}>
        <Icon name="lock" size={22} />
        <h2>Importar dados é só do Admin</h2>
        <p className="sm">
          Uma importação cria e altera muitos registros de uma vez (filamentos, carretéis, insumos,
          produtos, despesas). Por isso só o Lucas importa. Se você tem uma planilha para entrar no
          sistema, mande para ele.
        </p>
        <p className="xs muted">Importações já feitas aparecem na aba Auditoria.</p>
        <Link className="btn sm" to="/configuracoes/dados/auditoria">
          Ver auditoria
        </Link>
      </section>
    );
  return <ImportWizard />;
}

function ImportWizard() {
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [entity, setEntity] = useState<ImportEntity>('carreteis');
  const [file, setFile] = useState<Loaded | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [keyField, setKeyField] = useState<string | null>(null);
  const [fixes, setFixes] = useState<{ line: number; column: string; value: string }[]>([]);
  const [create, setCreate] = useState<string[]>([]);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [editing, setEditing] = useState<{ line: number; column: string; value: string } | null>(
    null,
  );
  const [tried, setTried] = useState(false);
  const [result, setResult] = useState<ImportRecord | null>(null);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [idem, setIdem] = useState(newIdempotencyKey);
  const spec = useQuery({
    queryKey: ['imports', 'spec', entity],
    queryFn: () => api.get(`/imports/spec/${entity}`, ImportSpec),
  });
  const recent = useQuery({
    queryKey: ['imports', 'recent'],
    queryFn: () => api.get('/imports', z.object({ data: z.array(ImportRecord) })),
  });
  const body = (
    over: Partial<{ mapping: Record<string, string>; fixes: typeof fixes; create: string[] }> = {},
  ) => ({
    entity,
    fileName: file?.name ?? 'arquivo.csv',
    csv: file?.text ?? '',
    mapping: over.mapping ?? mapping,
    keyField,
    fixes: over.fixes ?? fixes,
    create: over.create ?? create,
  });
  const simulate = useMutation({
    mutationFn: (b: ReturnType<typeof body>) => api.post('/imports/preview', ImportPreview, b),
    onSuccess: (p) => {
      setPreview(p);
      setMapping(p.mapping);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const run = useMutation({
    mutationFn: () => api.post('/imports', ImportRecord, body(), { idempotencyKey: idem }),
    onSuccess: (r) => {
      setResult(r);
      setStep(3);
      void qc.invalidateQueries();
      toast(
        `${r.newCount + r.updatedCount} ${IMPORT_ENTITY_LABELS[entity].toLowerCase()} importados`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const undo = useMutation({
    mutationFn: (id: string) => api.post(`/imports/${id}/undo`, ImportRecord),
    onSuccess: (r) => {
      setResult((cur) => (cur?.id === r.id ? r : cur));
      setConfirmUndo(false);
      void qc.invalidateQueries();
      toast('Importação desfeita. Está na Lixeira por 30 dias.');
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const reset = () => {
    setStep(0);
    setFile(null);
    setMapping({});
    setFixes([]);
    setCreate([]);
    setPreview(null);
    setResult(null);
    setTried(false);
    setIdem(newIdempotencyKey());
  };
  const resim = (over: Parameters<typeof body>[0]) => simulate.mutate(body(over));
  const fields = spec.data?.fields ?? [];
  const label = IMPORT_ENTITY_LABELS[entity];

  return (
    <section className="card col" style={{ gap: 16 }}>
      <div className="row between wrap" style={{ gap: 10 }}>
        <h2>Importar {label.toLowerCase()}</h2>
        {file ? (
          <span className="sm muted">
            {file.name} · {file.rows} {file.rows === 1 ? 'linha' : 'linhas'}
          </span>
        ) : null}
      </div>
      <nav className="row wrap" aria-label="Passos" style={{ gap: 6 }}>
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            className={i === step ? 'chip on' : 'chip'}
            aria-current={i === step ? 'step' : undefined}
            disabled={i > step || step === 3}
            onClick={() => setStep(i)}
          >
            {i + 1} · {s}
          </button>
        ))}
      </nav>

      {step === 0 ? (
        <div className="col" style={{ gap: 12 }}>
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="imp-entity">O que você vai importar</label>
            <select
              id="imp-entity"
              className="input"
              value={entity}
              onChange={(e) => {
                setEntity(ImportEntitySchema.parse(e.target.value));
                setMapping({});
                setPreview(null);
              }}
            >
              {ImportEntitySchema.options.map((e) => (
                <option key={e} value={e}>
                  {IMPORT_ENTITY_LABELS[e]}
                </option>
              ))}
            </select>
          </div>
          <input
            ref={input}
            type="file"
            hidden
            accept=".csv,text/csv,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              void f.text().then((t) => {
                setFile(describeFile(f.name, t));
                setMapping({});
                setFixes([]);
                setCreate([]);
                setPreview(null);
              });
            }}
          />
          {file ? (
            <div className="card sunk row" style={{ gap: 12, padding: 14 }}>
              <div className="grow">
                <b>{file.name}</b>
                <p className="xs muted">
                  {file.rows} linhas · {file.columns} colunas · separador {file.separator}
                </p>
              </div>
              <button type="button" className="btn sm" onClick={() => input.current?.click()}>
                Trocar arquivo
              </button>
            </div>
          ) : (
            <div className="dropzone">
              <span>Arraste o arquivo CSV aqui</span>
              <span className="xs muted">
                Ponto e vírgula ou vírgula, UTF-8. Planilha do Excel? Salve como CSV.
              </span>
              <button type="button" className="btn sm" onClick={() => input.current?.click()}>
                Escolher arquivo
              </button>
            </div>
          )}
          {tried && !file ? (
            <span className="help err">Escolha um arquivo para continuar.</span>
          ) : null}
          <div className="row between wrap" style={{ gap: 10 }}>
            <a className="btn g" href={`/api/v1/imports/templates/${entity}-modelo.csv`} download>
              Baixar modelo de {label.toLowerCase()} (.csv)
            </a>
            <button
              type="button"
              className="btn p"
              disabled={simulate.isPending}
              onClick={() => {
                setTried(true);
                if (!file) return;
                setKeyField(spec.data?.keyField ?? null);
                simulate.mutate(
                  { ...body({ mapping: {} }), keyField: spec.data?.keyField ?? null },
                  { onSuccess: () => setStep(1) },
                );
              }}
            >
              Continuar
            </button>
          </div>
        </div>
      ) : null}

      {step === 1 && preview ? (
        <div className="col" style={{ gap: 12 }}>
          <div>
            <h3>Colunas do arquivo</h3>
            <p className="sm muted">Diga o que é cada coluna. Lembramos para a próxima vez.</p>
          </div>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '1fr 1fr 1.2fr' }}>
              <span>Coluna no arquivo</span>
              <span>Exemplo (linha 2)</span>
              <span>Vai para</span>
            </div>
            {preview.columns.map((c) => (
              <div key={c} className="gtr" style={{ gridTemplateColumns: '1fr 1fr 1.2fr' }}>
                <span className="tab">{c}</span>
                <span className="sm muted">{preview.sample[c] || '(vazio)'}</span>
                <select
                  className="input"
                  aria-label={`Destino de ${c}`}
                  value={mapping[c] ?? 'ignore'}
                  onChange={(e) => setMapping({ ...mapping, [c]: e.target.value })}
                >
                  {fields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                      {f.required ? ' *' : ''}
                    </option>
                  ))}
                  <option value="ignore">Ignorar coluna</option>
                </select>
              </div>
            ))}
          </div>
          {spec.data?.keys.length ? (
            <div className="field" style={{ maxWidth: 420 }}>
              <label htmlFor="imp-key">Coluna-chave</label>
              <select
                id="imp-key"
                className="input"
                value={keyField ?? ''}
                onChange={(e) => setKeyField(e.target.value || null)}
              >
                {spec.data.keys.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
                <option value="">Nenhuma: tudo entra como novo</option>
              </select>
            </div>
          ) : null}
          {preview.mappingErrors.length ? (
            <Alert tone="err">{preview.mappingErrors.join(' ')}</Alert>
          ) : null}
          <div className="row between wrap" style={{ gap: 10 }}>
            <button type="button" className="btn" onClick={() => setStep(0)}>
              Voltar ao arquivo
            </button>
            <button
              type="button"
              className="btn p"
              disabled={simulate.isPending}
              onClick={() =>
                simulate.mutate(body(), {
                  onSuccess: (p) => {
                    if (!p.mappingErrors.length) setStep(2);
                  },
                })
              }
            >
              Simular importação
            </button>
          </div>
        </div>
      ) : null}

      {step === 2 && preview ? (
        <div className="col" style={{ gap: 14 }}>
          <div className="grid s">
            <div className="card" style={{ borderColor: 'var(--ok)' }}>
              <p className="xs muted">Novos</p>
              <p className="fr tab" style={{ fontSize: 26, color: 'var(--ok)' }}>
                {preview.counts.new}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Atualizados pela chave</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {preview.counts.update}
              </p>
            </div>
            <div
              className="card"
              style={{ borderColor: preview.counts.errors ? 'var(--err)' : 'var(--ok)' }}
            >
              <p className="xs muted">Com erro</p>
              <p
                className="fr tab"
                style={{ fontSize: 26, color: preview.counts.errors ? 'var(--err)' : 'var(--ok)' }}
              >
                {preview.counts.errors}
              </p>
            </div>
          </div>
          <p className="sm muted">
            Nada foi gravado ainda. A simulação lê o arquivo inteiro e mostra o que aconteceria.
          </p>
          {preview.notes.map((n) => (
            <Alert key={n} tone="info">
              {n}
            </Alert>
          ))}
          <section className="card flat">
            <div className="row between wrap" style={{ padding: '12px 16px', gap: 8 }}>
              <h3>Erros</h3>
              {preview.issues.length ? (
                <a
                  className="btn sm"
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(errorsCsv(preview.issues))}`}
                  download="erros-importacao.csv"
                >
                  Baixar relatório de erros
                </a>
              ) : null}
            </div>
            {preview.issues.length === 0 ? (
              <p className="sm" style={{ padding: '0 16px 14px' }}>
                <b>Sem erros.</b> Todas as {preview.rows} linhas podem entrar.
              </p>
            ) : (
              <div className="gt" style={{ overflowX: 'auto' }}>
                <div
                  className="gtr head"
                  style={{ gridTemplateColumns: '60px 120px 1fr 2fr 200px', minWidth: 640 }}
                >
                  <span>Linha</span>
                  <span>Coluna</span>
                  <span>Valor</span>
                  <span>Problema</span>
                  <span />
                </div>
                {preview.issues.map((i) => {
                  const isEditing = editing?.line === i.line && editing.column === i.column;
                  return (
                    <div
                      key={`${i.line}-${i.column}`}
                      className="gtr wl"
                      style={{ gridTemplateColumns: '60px 120px 1fr 2fr 200px', minWidth: 640 }}
                    >
                      <span className="tab">{i.line}</span>
                      <span className="tab sm">{i.column}</span>
                      {isEditing ? (
                        <input
                          className="input"
                          aria-label={`Novo valor da linha ${i.line}`}
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        />
                      ) : (
                        <span className="sm">{i.value || '(vazio)'}</span>
                      )}
                      <span className="sm">{i.problem}</span>
                      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              className="btn sm g"
                              onClick={() => setEditing(null)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => {
                                const next = [
                                  ...fixes.filter(
                                    (f) => !(f.line === i.line && f.column === i.column),
                                  ),
                                  editing,
                                ];
                                setFixes(next);
                                setEditing(null);
                                resim({ fixes: next });
                                toast(`Linha ${i.line} corrigida`);
                              }}
                            >
                              Aplicar
                            </button>
                          </>
                        ) : i.fix.kind === 'replace' ? (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => {
                              const next = [
                                ...fixes,
                                {
                                  line: i.line,
                                  column: i.column,
                                  value: i.fix.kind === 'replace' ? i.fix.value : '',
                                },
                              ];
                              setFixes(next);
                              resim({ fixes: next });
                              toast(`Linha ${i.line} corrigida`);
                            }}
                          >
                            {i.fix.label}
                          </button>
                        ) : i.fix.kind === 'create' ? (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => {
                              const next = [...create, i.fix.kind === 'create' ? i.fix.value : ''];
                              setCreate(next);
                              resim({ create: next });
                            }}
                          >
                            {i.fix.label}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() =>
                              setEditing({ line: i.line, column: i.column, value: i.value })
                            }
                          >
                            Editar
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          {preview.preview.length ? (
            <section className="card flat">
              <div style={{ padding: '12px 16px' }}>
                <h3>Prévia · primeiras linhas</h3>
              </div>
              <div className="gt" style={{ overflowX: 'auto' }}>
                <div
                  className="gtr head"
                  style={{
                    gridTemplateColumns: `90px repeat(${preview.previewHeader.length}, 1fr)`,
                    minWidth: 560,
                  }}
                >
                  <span>Ação</span>
                  {preview.previewHeader.map((h) => (
                    <span key={h}>{h}</span>
                  ))}
                </div>
                {preview.preview.map((r) => (
                  <div
                    key={r.line}
                    className="gtr"
                    style={{
                      gridTemplateColumns: `90px repeat(${preview.previewHeader.length}, 1fr)`,
                      minWidth: 560,
                    }}
                  >
                    <span>
                      <span className={r.action === 'update' ? 'badge info' : 'badge ok'}>
                        {r.action === 'update' ? 'Atualizar' : 'Novo'}
                      </span>
                    </span>
                    {r.values.map((v, k) => (
                      <span key={k} className="sm">
                        {v}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <div className="row between wrap" style={{ gap: 10 }}>
            <div className="row wrap" style={{ gap: 10 }}>
              <button type="button" className="btn" onClick={() => setStep(1)}>
                Voltar às colunas
              </button>
              <button type="button" className="btn g" onClick={reset}>
                Reenviar arquivo corrigido
              </button>
            </div>
            <button
              type="button"
              className="btn p"
              disabled={run.isPending || preview.counts.new + preview.counts.update === 0}
              onClick={() => run.mutate()}
            >
              Importar {preview.counts.new + preview.counts.update} linhas válidas
            </button>
          </div>
        </div>
      ) : null}

      {step === 3 && result ? (
        <div className="col" style={{ gap: 14 }}>
          {result.status === 'undone' ? (
            <Alert tone="info">
              <b>Importação desfeita.</b> Os registros foram para a Lixeira e ficam lá por 30 dias.
            </Alert>
          ) : (
            <Alert tone="ok">
              <b>
                {result.newCount + result.updatedCount} {label.toLowerCase()} importados.
              </b>{' '}
              Hoje às {formatTime(result.createdAt)}, por {result.actor}. Dá para desfazer até{' '}
              {formatDate(result.undoUntil)}.
            </Alert>
          )}
          <div className="grid s">
            <div className="card">
              <p className="xs muted">Novos</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {result.newCount}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Atualizados</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {result.updatedCount}
              </p>
            </div>
            <div className="card">
              <p className="xs muted">Ignorados por erro</p>
              <p className="fr tab" style={{ fontSize: 26 }}>
                {result.skippedCount}
              </p>
            </div>
          </div>
          <div className="row wrap" style={{ gap: 10 }}>
            <Link className="btn" to={LINKS[entity]}>
              Ver {label.toLowerCase()}
            </Link>
            {result.status === 'done' ? (
              <button type="button" className="btn d" onClick={() => setConfirmUndo(true)}>
                Desfazer importação
              </button>
            ) : (
              <Link className="btn" to="/configuracoes/dados/lixeira">
                Ver na Lixeira
              </Link>
            )}
            <button type="button" className="btn g" onClick={reset}>
              Importar outro arquivo
            </button>
          </div>
        </div>
      ) : null}

      {recent.data?.data.length ? (
        <div className="col" style={{ gap: 6 }}>
          <span className="lbl">Importações recentes</span>
          {recent.data.data.slice(0, 5).map((r) => (
            <div key={r.id} className="kv sm">
              <span>
                {IMPORT_ENTITY_LABELS[r.entity]} · {r.fileName} · {formatDate(r.createdAt)}
                <span className="xs muted" style={{ display: 'block' }}>
                  {r.newCount} novos, {r.updatedCount} atualizados · {r.actor}
                </span>
              </span>
              <span className="v">
                {r.status === 'undone' ? (
                  <span className="badge">desfeita</span>
                ) : r.undoable ? (
                  <button
                    type="button"
                    className="btn sm g"
                    disabled={undo.isPending}
                    onClick={() => undo.mutate(r.id)}
                  >
                    Desfazer
                  </button>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {confirmUndo && result ? (
        <Modal
          title="Desfazer a importação?"
          onClose={() => setConfirmUndo(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmUndo(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn d"
                disabled={undo.isPending}
                onClick={() => undo.mutate(result.id)}
              >
                Desfazer
              </button>
            </>
          }
        >
          <p>
            Os {result.newCount + result.updatedCount} registros criados ou alterados por este
            arquivo voltam ao que eram: os novos vão para a Lixeira, onde você pode restaurar por 30
            dias.
          </p>
        </Modal>
      ) : null}
    </section>
  );
}
